import type { OrderDTO } from 'ecosystem'
import storefrontApi from 'api/storefront-api'
import { type BackendOrderLines } from './types'

/**
 * Persist the PSP's session id as the SF order's `paymentId`.
 *
 * Needed by the providers whose confirmation URL carries no template variable
 * (Svea's `ConfirmationUri`, Briqpay's `urls.redirect`): the confirmation page
 * only ever knows the SF order number, so the session id has to be recoverable
 * from the order itself or the purchase cannot be provisioned. Failing this
 * write aborts the create rather than leaving an order with no link to its
 * payment.
 *
 * `PUT /storefront/orders` is a full replace, not a merge, and the create
 * response only echoes a minimal projection (no items, no customer) — so the
 * already-prepared lines are passed back through untouched. `id` is stripped
 * because the BE matches by `orderNumber`.
 */
export const persistPaymentId = async (
  backendOrder: OrderDTO,
  paymentId: string,
  backendItems: BackendOrderLines
) => {
  if (!backendOrder.orderNumber) {
    throw new Error('SF order is missing orderNumber — cannot persist paymentId')
  }

  const { id: _id, customer: _customer, items: _items, ...rest } = backendOrder

  await storefrontApi.orders.updateStorefrontOrder({
    ...rest,
    items: backendItems,
    paymentId
  })
}
